/**
 * Store pour la gestion de l'utilisateur authentifié
 */

import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import axios from '@js/tools/axios';
/***/
import { useManagingUser } from '@compositions/ManagingUser';
import Storage, { STORAGE_KEYS } from '@classes/Storage.js';

export const useAuthUserStore = defineStore('authUser', (() => {

    // Composition pour l'utilisateur que l'on gère
    const { updateManagingUser } = useManagingUser({});

    // State de l'utilisateur authentifié
    // La valeur est à false tant que l'utilisateur n'a pas été chargé
    const user = ref(false);

    // State des demandes de congés reçus par un manager
    const requestedHolidaysReceived = ref([]);

    // State de la liste des demandes de congés reçus par un manager (total, requests)
    const requestedHolidaysReceivedList = ref({});

    // State de la liste des demandes de congés d'un agent (total, requests)
    const requestedHolidaysList = ref({});

    // Vrai si l'utilisateur a caché le temps restant de la période d'essai
    const hiddenTrialPeriodRemainingTime = ref(Storage().get(STORAGE_KEYS.HIDDEN_TRIAL_PERIOD_REMAINING_TIME, false));

    /**
     * Retourne l'utilisateur connecté
     * @return object|null
     */
    const getUser = computed(() => user.value);

    /**
     * Retourne le nombre total de crédits de l'utilisateur
     * @return int|null
     */
    const getTotalCredits = computed(() => {
        const user = getUser.value;
        if(! user || ! user.credits) {
            return null;
        }
        const values = Object.keys(user.credits).map((key) => user.credits[key]);
        return values.reduce(((value1, value2) => value1 + value2), 0);
    });

    /**
     * Retourne la liste des demandes de congés reçus
     * @return object
     */
    const getRequestedHolidaysReceivedList = computed(() => requestedHolidaysReceivedList.value || {});

    /**
     * Retourne la liste des demandes de congés
     * @return object
     */
    const getRequestedHolidaysList = computed(() => requestedHolidaysList.value || {});

        /**
     * Retourne si l'utilisateur a le rôle super admin
     * @return bool
     */
    const isSuperAdmin = computed(() => {
        const authUser = user.value;
        return (authUser && authUser.role && authUser.role && authUser.role.isSuperAdmin === true);
    });

    /**
     * Retourne si l'utilisateur connecté a la permission en paramètre
     * @param string permission
     * @return bool
     */
    const hasPermission = computed(() => {
        return ((permission) => {
            const authUser = user.value;
            if(! authUser || ! authUser.role || ! authUser.role.permissions) {
                return false;
            }
            return (authUser.role.permissions.indexOf(permission) !== -1)

        });
    });

    /**
     * Retourne si un utilisateur est connecté
     * @return boolean
     */
    const authenticated = computed(() => {
        const authUser = user.value || null;
        const userId = (authUser !== null) ? authUser.id : null;
        return (userId !== null);
    });

    /**
     * Retourne si l'utilisateur a caché le temps restant de la période d'essai
     * return boolean
     */
    const getHiddenTrialPeriodRemainingTime = computed(() => hiddenTrialPeriodRemainingTime.value);

    /**
     * Cache le temps restant de la période d'essai
     * @return void
     */
    const hideTrialPeriodRemainingTime = (() => {
        Storage().set(STORAGE_KEYS.HIDDEN_TRIAL_PERIOD_REMAINING_TIME, true);
        hiddenTrialPeriodRemainingTime.value = true;
    });

    /**
     * Affiche le temps restant de la période d'essai
     * @return void
     */
    const displayTrialPeriodRemainingTime = (() => {
        Storage().set(STORAGE_KEYS.HIDDEN_TRIAL_PERIOD_REMAINING_TIME, false);
        hiddenTrialPeriodRemainingTime.value = false;
    });

    /**
     * Chargement des données de l'utilisateur connecté
     * @param bool force Vrai si on force le rechargement des données
     * @return Promise
     */
    const getCurrentUser = ((force = false) => {

        if(user.value !== false && ! force) {
            return Promise.resolve(user.value);
        }

        return axios.get('/user/current').then((response) => {
            if(response.data.success && response.data.user !== undefined) {
                setCurrentUser(response.data.user);
                return Promise.resolve(user.value);
            }
        });

    });

    /**
     * Modifie l'utilisateur connecté
     * @param object|null data
     * @return void
     */
    const setCurrentUser = ((data) => {
        user.value = data;
        if(data) {
            updateManagingUser(data);
        }
    });

    /**
     * Met à jour les données de l'utilisateur
     * @param object|null data
     * @return void
     */
    const updateCurrentUserData = ((data) => {
        if(user.value) {
            user.value = { ...user.value, ...data };
        }
    });

    /**
     * Déconnexion de l'utilisateur
     * @return Promise
     */
    const logout = (() => {
        return axios.get('/auth/logout').then(() => {
            setCurrentUser(null);
            return Promise.resolve();
        });
    });

    /**
     * Chargement de la liste des demandes de congés reçus par le manager connecté
     * @param object params Paramètres pour la requête Ajax
     * @return Promise
     */
    const loadRequestedHolidaysReceivedList = ((params) => {

        const path = '/api/admin/users/requested-holidays-received';
        return axios.get(path, {
            params: params
        }).then((response) => {
            if(response && response.data && response.data.success && response.data.data) {

                if(params.offset === 0) {
                    resetRequestedHolidaysReceivedList();
                }

                updateRequestedHolidaysReceivedList(response.data.data);
                return Promise.resolve();
            } else {
                return Promise.reject();
            }
        });
    });

    /**
     * Chargement de la liste des demandes de congés de l'agent connecté
     * @param object params Paramètres pour la requête Ajax
     * @return Promise
     */
    const loadRequestedHolidaysList = ((params) => {

        const path = '/api/admin/users/me/request-holidays';

        return axios.get(path, {
            params: params
        }).then((response) => {
            if(response && response.data && response.data.success && response.data.data) {

                if(params.offset === 0) {
                    resetRequestedHolidaysList();
                }

                updateRequestedHolidaysList(response.data.data);
                return Promise.resolve();
            } else {
                return Promise.reject();
            }
        });

    });

    /**
     * Répond à la demande de congés reçue
     * @param object request La demande
     * @param bool accept
     * @param string message Message de refus
     * @return Promise
     */
    const answerRequestHolidaysReceived = ((request, accept, message = null) => {

        const params = {
            accept: accept
        };

        if(! accept) {
            params.message = message;
        }

        const path = '/api/admin/users/requested-holidays-received/:requestId/answer'.replace(':requestId', request.id);

        return axios.post(path, params).then((response) => {
            if(response && response.data && response.data.success && response.data.request) {
                updateRequestedHolidaysReceivedInList(response.data.request);
                return Promise.resolve();
            } else {
                return Promise.reject();
            }
        });

    });

    /**
     * Modifie une demande de congés reçue par le manager connecté
     * @param object request Données de la demande à mettre à jour dans la liste
     * @return Promise
     */
    const updateRequestedHolidaysReceived = ((request) => {

        const currentRequests = requestedHolidaysReceived.value;

        const index = currentRequests.findIndex((requestFromState) => requestFromState.id === request.id);
        if(index !== -1) {
            requestedHolidaysReceived.value[index] = { ...requestedHolidaysReceived.value[index], ...request };
        } else {
            requestedHolidaysReceived.value.push(request);
        }

    });

    /**
     * Mise à jour des données de la requête reçue dans la liste
     * @param Object request
     * @return void
     */
    const updateRequestedHolidaysReceivedInList = ((request) => {

        // Mise à jour dans les demandes reçues déjà chargées
        updateRequestedHolidaysReceived(request);

        // Recherche si la liste a été chargée
        const currentData = requestedHolidaysReceivedList.value;
        if(currentData.total === undefined) {
            return;
        }

        // Mise à jour de la demande dans la liste, si elle est présente
        const currentRequests = requestedHolidaysReceivedList.value.requests;
        const index = currentRequests.findIndex((requestFromList) => requestFromList.id === request.id);
        if(index !== -1) {
            const oldRequest = requestedHolidaysReceivedList.value.requests[index];
            requestedHolidaysReceivedList.value.requests[index] = { ...oldRequest, ...request };
        }
    });

    /**
     * Modifie la liste des demandes de congés reçus par le manager connecté
     * @param data Données des demandes total et items
     * @return void
     */
    const updateRequestedHolidaysReceivedList = ((data) => {

        const currentData = requestedHolidaysReceivedList.value;
        if(currentData.total === undefined) {
            requestedHolidaysReceivedList.value = {
                total: data.total,
                requests: data.items
            };

            data.items.forEach((request) => {
                updateRequestedHolidaysReceived(request);
            });

        } else {
            requestedHolidaysReceivedList.value.total = data.total;

            const currentRequests = requestedHolidaysReceivedList.value.requests;
            const newRequests = data.items.filter((item) => ! currentRequests.find((current) => current.id === item.id));
            requestedHolidaysReceivedList.value.requests = [ ...currentRequests, ...newRequests ];

            newRequests.forEach((request) => {
                updateRequestedHolidaysReceived(request);
            });
        }
    });

    /**
     * Modifie la liste des demandes de congés de l'agent connecté
     * @param data Données des demandes : total et items
     * @return void
     */
    const updateRequestedHolidaysList = ((data) => {

        const currentData = requestedHolidaysList.value;
        if(currentData.total === undefined) {
            requestedHolidaysList.value = {
                total: data.total,
                requests: data.items
            };

        } else {
            requestedHolidaysList.value.total = data.total;

            const currentRequests = requestedHolidaysList.value.requests;
            const newRequests = data.items.filter((item) => ! currentRequests.find((current) => current.id === item.id));
            requestedHolidaysList.value.requests = [ ...currentRequests, ...newRequests ];
        }

    });

    /**
     * Met à jour une demande de congés
     * @param data Données de la demande à mettre à jour
     * @return void
     */
    const updateRequestedHolidays = ((data) => {
        const currentData = requestedHolidaysList.value;
        if(currentData.total === undefined) {
            return;
        }

        const currentRequests = requestedHolidaysList.value.requests;
        const currentIndex = currentRequests.findIndex((request) => request.id === data.id);
        if(currentIndex === -1) {
            requestedHolidaysList.value.total += 1;
            requestedHolidaysList.value.requests.push(data);
        } else {
            currentRequests[currentIndex] = { ...currentRequests[currentIndex], ...data };
        }

    });

    /**
     * Supprime une demande de congés
     * @param data Données de la demande à supprimer
     * @return Promise
     */
    const deleteRequestedHoliday = ((data) => {

        const path = '/api/admin/users/me/request-holidays/' + data.id;

        return axios.delete(path).then((response) => {
            if(response && response.data && response.data.success) {
                if(response.data.user) {
                    updateCurrentUserData(response.data.user);
                }
                onDeleteRequestedHoliday(data);
                return Promise.resolve();
            } else {
                return Promise.reject();
            }
        });

    });

    /**
     * Met à jour la liste des demandes de congés après la suppression d'une demande
     * @param data Données de la demande qui a été supprimé
     * @return Promise
     */
    const onDeleteRequestedHoliday = ((data) => {
        const currentData = requestedHolidaysList.value;
        if(currentData.total === undefined) {
            return;
        }

        const currentRequests = requestedHolidaysList.value.requests;
        const currentIndex = currentRequests.findIndex((request) => request.id === data.id);
        if(currentIndex !== -1) {
            requestedHolidaysList.value.total -= 1;
            requestedHolidaysList.value.requests.splice(currentIndex, 1);
        }
    });

    /**
     * Réinitialisation de la liste des demandes de congés reçues par le manager connecté
     * @return void
     */
    const resetRequestedHolidaysReceivedList = (() => {
        requestedHolidaysReceivedList.value = {};
    });

    /**
     * Réinitialisation de la liste des demandes de congés de l'agent connecté
     * @return void
     */
    const resetRequestedHolidaysList = (() => {
        requestedHolidaysList.value = {};
    });

    /**
     * Désactive l'aide à la validation d'une tâche
     * @return Promise
     */
    const disableValidationTaskHelp = (() => {

        const path = '/api/me/account/settings/disable-validation-task-help';

        return axios.post(path).then((response) => {
            if(response && response.data && response.data.success && response.data.user) {
                updateCurrentUserData(response.data.user);
                return Promise.resolve();
            }
            return Promise.reject();
        });

    });

    /**
     * Désactive le message sur la fenêtre d'édition d'une charte graphique concernant la résolution de l'image
     * @return Promise
     */
    const disableImageResolutionEditorMessage = (() => {

        const path = '/api/me/account/settings/disable-image-editor-resolution-message';
        return axios.post(path).then((response) => {
            if(response && response.data && response.data.success && response.data.user) {
                updateCurrentUserData(response.data.user);
                return Promise.resolve();
            }
            return Promise.reject();
        });

    });

    /**
     * Dé-liaison d'un réseau du compte d'un utilisateur
     * @param string network
     * @return Promise
     */
    const unlinkNetwork = ((network) => {

        const path = `/auth/social/${ network.toLowerCase() }`;

        return axios.delete(path).then((response) => {
            if(response.data.success) {
                const userData = Object.assign({}, JSON.parse(JSON.stringify(user.value)));
                const identities = (userData.socialNetworkIdentities ?? []).filter((identity) => identity.network !== network.toUpperCase());
                user.value.socialNetworkIdentities = identities;
                return Promise.resolve();
            }
            return Promise.reject();
        }).catch((response) => {
            let errors = {};
            if(response && response.response && response.response.data && response.response.data.errors) {
                errors = response.response.data.errors;
            }
            return Promise.reject(errors);
        });

    });

    /**
     * Modifie le langage de l'application de l'utilisateur
     * @param string newLanguage
     * @return Promise
     */
    const changeLanguage = ((newLanguage) => {

        const path = '/api/me/account/change-language';

        const requestParams = {
            lang: newLanguage
        };

        return axios.post(path, requestParams).then((response) => {
            if (response && response.data && response.data.success && response.data.user) {
                updateCurrentUserData(response.data.user);
                return Promise.resolve();
            }
            return Promise.reject();
        });

    });

    /**
     * Suppression du compte
     * @return Promise
     */
    const removeAccount = (() => {

        const path = '/api/me/account';

        return axios.delete(path).then((response) => {
            const success = (response?.data?.success ?? false);
            if(success === true) {
                return Promise.resolve();
            }
            return Promise.reject();
        });

    });

    return {
        getUser,
        isSuperAdmin,
        hasPermission,
        authenticated,
        getCurrentUser,
        setCurrentUser,
        updateCurrentUserData,
        logout,
        /***/
        getRequestedHolidaysList,
        loadRequestedHolidaysList,
        updateRequestedHolidays,
        deleteRequestedHoliday,
        /***/
        getRequestedHolidaysReceivedList,
        loadRequestedHolidaysReceivedList,
        /***/
        answerRequestHolidaysReceived,
        updateRequestedHolidaysReceivedInList,
        /***/
        getTotalCredits,
        /***/
        disableValidationTaskHelp,
        disableImageResolutionEditorMessage,
        /***/
        unlinkNetwork,
        changeLanguage,
        removeAccount,
        getHiddenTrialPeriodRemainingTime,
        hideTrialPeriodRemainingTime,
        displayTrialPeriodRemainingTime
    }
}));

